import React, { FC, useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Alert,
    Autocomplete,
    TextField,
    InputAdornment,
    Grid,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useTranslation } from 'react-i18next';
import { DataTableHeadCell } from '../../data-table/data-table.componet';
import ProductionValueReportRow from './production-value-report-row.component';
import InfoText from 'components/shared/info-text.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { ProductionStep } from 'models/order';
import { User } from 'models/user';
import { ProductionValueTemplate, ProductionValueStep } from 'models/organization';

type WorkflowStep = {
    _id: string;
    name: string;
    department: string;
    technicians?: User[];
};

type TemplateDepartment = {
    _id: string;
    name: string;
    technicians: any[];
};

type ProductionValueReportTableProps = {
    workflowStepOptions: WorkflowStep[];
    steps: ProductionStep[];
    isProductionFinished: boolean;
    fromInternalPage?: boolean;
    isTechnicianAdmin: boolean;
    valueTemplates?: ProductionValueTemplate[];
    productionValueSteps?: ProductionValueStep[];
    departments?: TemplateDepartment[];
    actionDeductionValue?: number;
    onStepsChange?: (steps: ProductionStep[]) => void;
    onValidationChange?: (isValid: boolean) => void;
};

const ProductionValueReportTable: FC<ProductionValueReportTableProps> = ({
    workflowStepOptions,
    steps,
    isProductionFinished,
    fromInternalPage,
    isTechnicianAdmin,
    valueTemplates = [],
    productionValueSteps = [],
    departments = [],
    actionDeductionValue = 0,
    onStepsChange,
    onValidationChange,
}) => {
    const { t: translate } = useTranslation(['orders', 'lab-settings', 'common']);
    const [pendingTemplate, setPendingTemplate] = useState<ProductionValueTemplate | null>(null);

    const isEditingDisabled = isProductionFinished && fromInternalPage && !isTechnicianAdmin;

    const handleStepChange = (rawIndex: number, field: string, value: any) => {
        const updatedSteps = steps.map((step, index) => (index === rawIndex ? { ...step, [field]: value } : step));
        onStepsChange?.(updatedSteps);
    };

    const handleDeleteStep = (rawIndex: number) => {
        const updatedSteps = steps.filter((_, index) => index !== rawIndex);
        onStepsChange?.(updatedSteps);
    };

    const handleAddStep = (stepId: string) => {
        if (!stepId) return;
        const step = workflowStepOptions.find((s: WorkflowStep) => s._id === stepId);
        if (!step) return;
        const newStep = {
            department: step.department || '',
            step: step.name,
            valuePercent: 0,
            technician: '',
            technicians: step.technicians,
            stepValue: 0,
        };
        onStepsChange?.([...steps, newStep]);
    };

    const buildStepsFromTemplate = (template: ProductionValueTemplate): ProductionStep[] => {
        const sorted = [...template.steps].sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));
        return sorted.map((templateStep) => {
            const pvStep = productionValueSteps.find((s) => s._id === templateStep.stepId);
            const dept = pvStep?.departmentId ? departments.find((d) => d._id === pvStep.departmentId) : undefined;
            return {
                step: pvStep?.name || '',
                department: dept?.name || '',
                valuePercent: Number(templateStep.valuePercent) || 0,
                technician: '',
                technicians: dept?.technicians || [],
                stepValue: 0,
            };
        });
    };

    const handleSelectTemplate = (template: ProductionValueTemplate | null) => {
        if (!template) return;
        if (steps.length > 0) {
            setPendingTemplate(template);
        } else {
            onStepsChange?.(buildStepsFromTemplate(template));
        }
    };

    const handleConfirmApplyTemplate = () => {
        if (pendingTemplate) {
            onStepsChange?.(buildStepsFromTemplate(pendingTemplate));
        }
        setPendingTemplate(null);
    };

    const calculateTotal = (stepsToSum: ProductionStep[]) =>
        stepsToSum.reduce((sum, s) => sum + (Number(s.valuePercent) || 0), 0);

    const totalPercent = calculateTotal(steps);
    const isTotalInvalid = totalPercent !== 100;
    const hasNoSteps = steps.length === 0;
    const hasMissingTechnician = steps.some((step) => !step.technician);

    useEffect(() => {
        const isValid = !hasNoSteps && !hasMissingTechnician && !isTotalInvalid;
        onValidationChange?.(isValid);
    }, [hasNoSteps, hasMissingTechnician, isTotalInvalid]);

    return (
        <TableContainer>
            {!hasNoSteps && (!fromInternalPage || isTechnicianAdmin) && isProductionFinished && isTotalInvalid && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {`${translate('orders:TOTAL_VALUE_PERCENT_NOT_EXACTLY')} ${totalPercent}%`}
                </Alert>
            )}
            {isProductionFinished && hasNoSteps && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {translate('lab-settings:NO_STEPS_ADDED')}
                </Alert>
            )}
            <Table>
                <TableHead>
                    <TableRow>
                        <DataTableHeadCell>{translate('common:STEP')}</DataTableHeadCell>
                        <DataTableHeadCell>{translate('orders:DEPARTMENT')}</DataTableHeadCell>
                        <DataTableHeadCell width={200}>{translate('common:TECHNICIAN')}</DataTableHeadCell>
                        <DataTableHeadCell width={50}>{translate('orders:VALUE_PERCENT')}</DataTableHeadCell>
                        <DataTableHeadCell width={100}>{translate('orders:DEDUCTION_VALUE')}</DataTableHeadCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {steps.map((row, index: number) => (
                        <ProductionValueReportRow
                            key={index}
                            productionValueReportRow={row}
                            onStepChange={handleStepChange}
                            onDeleteStep={handleDeleteStep}
                            rawIndex={index}
                            isProductionFinished={isProductionFinished}
                            fromInternalPage={fromInternalPage}
                            isTechnicianAdmin={isTechnicianAdmin}
                            actionDeductionValue={actionDeductionValue}
                        />
                    ))}
                </TableBody>
            </Table>

            <Grid container spacing={1} alignItems="center" mt={1}>
                <Grid item xs={12} sm={6}>
                    {workflowStepOptions?.length ? (
                        <Autocomplete
                            fullWidth
                            disabled={isEditingDisabled}
                            options={workflowStepOptions}
                            groupBy={(option) => option.department}
                            getOptionLabel={(option) => option.name}
                            value={null}
                            onChange={(_, option) => {
                                if (option) handleAddStep(option._id);
                            }}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            size="small"
                            blurOnSelect
                            renderInput={(params) => (
                                <TextField {...params} placeholder={translate('lab-settings:ADD_STEP')} />
                            )}
                        />
                    ) : (
                        <InfoText text={translate('lab-settings:ALL_WORKFLOW_STEPS_ADDED')} />
                    )}
                </Grid>

                {valueTemplates.length > 0 && (
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            fullWidth
                            disabled={isEditingDisabled}
                            options={valueTemplates}
                            getOptionLabel={(option) => option.name}
                            value={null}
                            onChange={(_, value) => handleSelectTemplate(value)}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            size="small"
                            blurOnSelect
                            renderInput={(params) => (
                                <TextField {...params} placeholder={translate('orders:APPLY_TEMPLATE')} />
                            )}
                        />
                    </Grid>
                )}
            </Grid>

            <ConfirmDialog
                open={!!pendingTemplate}
                handleClose={() => setPendingTemplate(null)}
                handleConfirm={handleConfirmApplyTemplate}
                dialogTitle={translate('orders:APPLY_TEMPLATE')}
                dialogContent={translate('orders:CONFIRM_REPLACE_STEPS')}
            />
        </TableContainer>
    );
};

export default ProductionValueReportTable;
